import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import {
  CreateGoalOfLife,
  DeleteGoalOfLife,
  GetGoalOfLife,
  GetSharedGoalsSchema,
  MILESTONE_TITLE_MAX_LENGTH,
  SetGoalOfLifeDone,
  UpdateGoalOfLife,
} from "~/lib/schemas/goal-of-life";

export const getAllGoalByUserIdFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GetGoalOfLife)
  .handler(async ({ data, context }) => {
    const { getPublicUrl } = await import("~/lib/server/s3");
    const { getAllGoalByUserId } = await import("~/lib/server/goal-of-life");
    const userId = context.user.id;
    const rows = await getAllGoalByUserId(userId, data.filter);

    return rows.map((row) => {
      const key = row.categoryOfLife.imageUrl;
      if (!key) return row;

      return {
        ...row,
        categoryOfLife: {
          ...row.categoryOfLife,
          imageUrl: getPublicUrl(key),
        },
      };
    });
  });

export const createGoalOfLifeFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(CreateGoalOfLife)
  .handler(async ({ data, context }) => {
    const { createGoalOfLife } = await import("~/lib/server/goal-of-life");
    const { keyGrants, ...goal } = data;
    return createGoalOfLife(context.user.id, goal, keyGrants);
  });

export const updateGoalOfLifeFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(UpdateGoalOfLife)
  .handler(async ({ data, context }) => {
    const { updateGoalOfLife } = await import("~/lib/server/goal-of-life");
    const { id, keyGrants, ...goal } = data;
    return updateGoalOfLife(id, context.user.id, goal, keyGrants);
  });

export const setGoalOfLifeDoneFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(SetGoalOfLifeDone)
  .handler(async ({ data, context }) => {
    const { setGoalOfLifeDone } = await import("~/lib/server/goal-of-life");
    return setGoalOfLifeDone(data.id, context.user.id, data.done);
  });

export const deleteGoalOfLifeFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(DeleteGoalOfLife)
  .handler(async ({ data, context }) => {
    const { deleteGoalOfLife } = await import("~/lib/server/goal-of-life");
    const userId = context.user.id;
    return deleteGoalOfLife(data.id, userId);
  });

export const getSharedGoalsFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(GetSharedGoalsSchema)
  .handler(async ({ data, context }) => {
    const { getPublicUrl } = await import("~/lib/server/s3");
    const { getSharedGoals } = await import("~/lib/server/goal-of-life");
    const coachId = context.coach.id;

    const rows = await getSharedGoals(data.userId, coachId, { ...data.filter });

    return rows.map((row) => {
      const key = row.categoryOfLife.imageUrl;
      if (!key) return row;

      return {
        ...row,
        categoryOfLife: {
          ...row.categoryOfLife,
          imageUrl: getPublicUrl(key),
        },
      };
    });
  });

export const generateGoalShareSlugFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      goalId: z.string().uuid(),
      sharePlaintext: z.object({
        title: z.string(),
        description: z.string().nullable(),
        categoryName: z.string(),
        /** The steps' titles to publish with the goal. */
        steps: z.array(
          z.object({
            id: z.string().uuid(),
            title: z.string().max(MILESTONE_TITLE_MAX_LENGTH),
          }),
        ),
      }),
    }),
  )
  .handler(async ({ data, context }) => {
    const { generateGoalShareSlug } = await import("~/lib/server/goal-of-life");
    const userId = context.user.id;
    return generateGoalShareSlug(data.goalId, userId, data.sharePlaintext);
  });

export const removeGoalShareSlugFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ goalId: z.string().uuid() }))
  .handler(async ({ data, context }) => {
    const { removeGoalShareSlug } = await import("~/lib/server/goal-of-life");
    const userId = context.user.id;
    return removeGoalShareSlug(data.goalId, userId);
  });

export const getGoalByShareSlugFn = createServerFn({ method: "GET" })
  .validator(z.object({ shareSlug: z.string().min(10).max(10) }))
  .handler(async ({ data }) => {
    const { getGoalByShareSlug } = await import("~/lib/server/goal-of-life");
    return getGoalByShareSlug(data.shareSlug);
  });
